// Estilos da tela de login modernizada — cartão central dividido em painel
// institucional (azul) + formulário (branco), sobre fundo azul-marinho
// integral, conforme a referência visual aprovada (correção pós-homologação
// da Entrega LOGIN-2/2.1).
//
// Função pura — sem JSX, sem I/O. Recebe o `theme` do MUI (já com
// `theme.infoChat` da Entrega M2), devolve só objetos de estilo simples
// (CSS-in-JS) para `Login/index.js` mesclar em seu `useStyles`. Não decide
// nada de layout de dados (idiomas, cadastro, fundo customizado) — só como
// isso é apresentado visualmente.
//
// Usa exclusivamente os tokens de `theme.infoChat` (e a mesma função pura
// `createInfoChatDesignTokens` da M2, nunca modificada aqui) — nenhum hex
// literal novo neste arquivo.

import { createTheme } from "@material-ui/core/styles";
import { createInfoChatDesignTokens, hexToRgb } from "../../theme/infoChatDesignTokens";

// `rgba(...)`, nunca hex de 8 dígitos (`#RRGGBBAA`) — alguns parsers CSS
// (incluindo o do jsdom, usado nos testes) rejeitam hex com canal alfa e
// descartam a declaração inteira em silêncio. 0.8 de alfa equivale ao
// antigo sufixo hex "CC" (204/255).
const toTranslucentRgba = (hex, alpha) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// `theme.breakpoints`/`theme.spacing` são funções do MUI, não tokens do
// infoChat — precisam de um objeto de tema utilizável mesmo quando `theme`
// em si é `undefined`/`null`/incompleto (ex.: teste de fallback). Nesse
// caso, normalizamos um tema MUI mínimo só para fornecer essas duas
// utilidades — nunca aplicado a nenhuma tela, nunca usado para cor.
const hasUsableThemeUtilities = theme =>
  Boolean(theme && typeof theme.breakpoints?.down === "function" && typeof theme.spacing === "function");

const resolveSafeTheme = theme => (hasUsableThemeUtilities(theme) ? theme : createTheme());

const hasCompleteInfoChatTokens = tokens =>
  Boolean(
    tokens &&
      tokens.brand &&
      tokens.surfaces &&
      tokens.borders &&
      tokens.text &&
      tokens.shadows &&
      tokens.transitions &&
      tokens.radii &&
      tokens.spacing
  );

// Mesmo padrão de fallback seguro já usado em infoChatGlobalShellStyles.js
// (M3) — nunca deixa a tela de login quebrar caso theme.infoChat esteja
// ausente/incompleto, e nunca diverge dos tokens reais por reaproveitar a
// mesma função pura da M2.
const resolveInfoChatTokens = theme => {
  if (hasCompleteInfoChatTokens(theme?.infoChat)) {
    return theme.infoChat;
  }
  return createInfoChatDesignTokens(theme?.palette?.type, theme?.palette?.primary?.main);
};

// Largura máxima do CARTÃO INTEIRO (painel + formulário juntos), conforme a
// referência aprovada — não confundir com a antiga largura só do formulário
// (440px), que existia quando o formulário era um cartão independente.
const CARD_MAX_WIDTH = 960;
const INSTITUTIONAL_FLEX_BASIS = "45%";
const FORM_FLEX_BASIS = "55%";

/**
 * Estilos da tela de login modernizada.
 *
 * @param {object} theme - tema do MUI (idealmente já com `theme.infoChat`).
 *   Quando a empresa configura uma imagem de fundo (`appLogoBackgroundLight`/
 *   `Dark`), `Login/index.js` a compõe usando as camadas `customBgPhotoLayer`
 *   (foto) + `customBgWashLayer` (véu translúcido, cor definida aqui) — esta
 *   função não recebe mais nenhuma opção para isso.
 */
export const createInfoChatLoginStyles = theme => {
  const safeTheme = resolveSafeTheme(theme);
  const tokens = resolveInfoChatTokens(theme);

  // O painel institucional é um cartão corporativo sempre escuro — igual
  // nos dois modos do site (mesma composição do mockup aprovado), por
  // decisão de design, não por acaso. Continua vindo do MESMO sistema de
  // tokens da M2, só pedindo explicitamente a variante escura para este
  // elemento decorativo específico, com a cor institucional real da
  // empresa (nunca uma cor nova).
  const panelTokens = createInfoChatDesignTokens("dark", tokens.brand.primaryColor);

  return {
    cardMaxWidth: CARD_MAX_WIDTH,

    // Sempre valores válidos (tokens já vem do fallback seguro acima) —
    // para Login/index.js nunca precisar checar `theme.infoChat`
    // diretamente nem montar strings de borda "na mão".
    brandPrimaryColor: tokens.brand.primaryColor,
    borderSubtleColor: tokens.borders.subtle,

    // Fundo da PÁGINA INTEIRA (atrás do cartão) — sempre azul-marinho
    // (mesma superfície escura do painel institucional), independente do
    // modo claro/escuro do site E independente de qualquer imagem de fundo
    // customizada por empresa: é a composição aprovada na referência, e não
    // é mais configurável (correção pós-homologação — a foto customizada,
    // quando existe, passou a ficar restrita ao painel institucional, nunca
    // à página inteira).
    page: {
      backgroundColor: panelTokens.surfaces.canvas
    },

    // Container de página: centraliza o cartão vertical e horizontalmente
    // sobre o fundo azul-marinho.
    root: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      minHeight: "100vh",
      boxSizing: "border-box",
      padding: safeTheme.spacing(4),
      overflowX: "hidden",
      backgroundImage: [
        `radial-gradient(circle at 3% 12%, ${panelTokens.brand.primaryColor}1F 0, transparent 16%)`,
        `radial-gradient(circle at 94% 88%, ${panelTokens.brand.primaryColor}14 0, transparent 19%)`
      ].join(", "),
      backgroundRepeat: "no-repeat",
      [safeTheme.breakpoints.down("sm")]: {
        alignItems: "flex-start",
        padding: safeTheme.spacing(11, 2, 9)
      }
    },

    // O CARTÃO em si — painel institucional + formulário lado a lado,
    // cantos arredondados e sombra compartilhados (por isso `overflow:
    // hidden`: nenhum dos dois filhos precisa do próprio raio de borda).
    // Linha (desktop) / coluna (celular/tablet) — o painel institucional é
    // ocultado só por CSS abaixo de "sm", nunca por condicional em JS, para
    // nunca duplicar o formulário.
    shell: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
      maxWidth: CARD_MAX_WIDTH,
      margin: "0 auto",
      borderRadius: panelTokens.radii.lg,
      overflow: "hidden",
      boxShadow: panelTokens.shadows.lg,
      minHeight: 560,
      [safeTheme.breakpoints.down("sm")]: {
        flexDirection: "column",
        minHeight: "auto",
        borderRadius: panelTokens.radii.md
      }
    },

    institutionalPanel: {
      flex: `1 1 ${INSTITUTIONAL_FLEX_BASIS}`,
      position: "relative",
      overflow: "hidden",
      boxSizing: "border-box",
      color: panelTokens.text.primary,
      backgroundColor: panelTokens.surfaces.canvas,
      backgroundImage: `linear-gradient(145deg, ${panelTokens.brand.primaryColor} 0%, ${panelTokens.surfaces.raised} 58%, ${panelTokens.surfaces.canvas} 100%)`,
      transition: `background-color ${panelTokens.transitions.duration.base}ms ${panelTokens.transitions.easing.standard}`,
      [safeTheme.breakpoints.down("sm")]: {
        display: "none"
      }
    },

    // Camadas de fundo customizado por empresa: duas divs absolutamente
    // posicionadas cobrindo o painel inteiro, em vez de uma única
    // declaração combinando gradiente+foto via `style` inline. O jsdom
    // (ambiente de teste) rejeita silenciosamente `linear-gradient(...)`
    // quando atribuído via `element.style.backgroundImage` (funciona
    // normalmente em navegadores reais, mas o CSSOM do jsdom recusa o
    // valor inteiro) — `url(...)` e `rgba(...)` isolados funcionam em
    // ambos. A foto entra por baixo (`customBgPhotoLayer`, `backgroundImage`
    // definido em `Login/index.js` via style inline, só com `url(...)`), o
    // véu translúcido por cima (`customBgWashLayer`, cor fixa vinda daqui).
    customBgPhotoLayer: {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      backgroundSize: "cover",
      backgroundPosition: "center center",
      zIndex: 0
    },

    customBgWashLayer: {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      backgroundColor: toTranslucentRgba(panelTokens.surfaces.canvas, 0.8),
      zIndex: 1
    },

    // Conteúdo visível do painel (logo, títulos, ilustração) — sempre
    // acima das camadas de fundo customizado (`zIndex`). Recebe o padding
    // que antes vivia no próprio painel, para que as camadas de fundo
    // cubram o painel inteiro, de borda a borda, sem faixa da cor sólida
    // aparecendo ao redor da foto.
    institutionalPanelContent: {
      position: "relative",
      zIndex: 2,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: safeTheme.spacing(6),
      boxSizing: "border-box"
    },

    // Superfície neutra discreta atrás da logo, caso a marca configurada
    // não contraste bem com o painel escuro.
    logoBadge: {
      display: "inline-flex",
      alignSelf: "flex-start"
    },

    institutionalHeadline: {
      color: panelTokens.text.primary,
      fontWeight: 700
    },

    institutionalDescription: {
      color: panelTokens.text.secondary
    },

    // Único elemento gráfico decorativo do painel — o próprio ícone-marca do
    // InfoChat (o mesmo desenho de `backend/public/InfoChat.svg`, extraído
    // para `assets/infoChatIconMark.svg`), ampliado, e não um ícone de
    // estoque genérico. Puramente ilustrativo, sempre aria-hidden no
    // componente.
    decorationWrap: {
      display: "flex",
      justifyContent: "center",
      marginTop: safeTheme.spacing(5)
    },

    decorationCircle: {
      width: 210,
      height: 190,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: safeTheme.spacing(3),
      boxSizing: "border-box",
      pointerEvents: "none"
    },

    decorationImg: {
      width: "100%",
      height: "100%",
      objectFit: "contain",
      opacity: 0.9
    },

    formPanel: {
      flex: `1 1 ${FORM_FLEX_BASIS}`,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      backgroundColor: tokens.surfaces.base,
      color: tokens.text.primary,
      padding: safeTheme.spacing(6, 5),
      boxSizing: "border-box",
      transition: `background-color ${tokens.transitions.duration.base}ms ${tokens.transitions.easing.standard}`,
      [safeTheme.breakpoints.down("sm")]: {
        minHeight: 430,
        padding: safeTheme.spacing(5, 3)
      }
    },

    card: {
      backgroundColor: tokens.surfaces.base,
      color: tokens.text.primary,
      boxShadow: tokens.shadows.lg,
      borderRadius: tokens.radii.lg,
      border: `1px solid ${tokens.borders.subtle}`
    },

    cardHeadline: {
      color: tokens.text.primary,
      fontWeight: 700
    },

    cardSubtitle: {
      color: tokens.text.secondary
    },

    // Fixo no desktop (mesma composição aprovada). No mobile/altura reduzida
    // passa a `absolute` em relação à raiz da página (LOGIN-2.1) — deixa de
    // ficar "grudado" no viewport e passa a rolar junto com o conteúdo, para
    // nunca mais sobrepor o título/campos/botões do formulário. Respeita a
    // safe area superior do dispositivo. Idioma e tema agora ficam
    // agrupados neste único contêiner, no topo direito da página (correção
    // pós-homologação: antes ficavam em cantos opostos).
    topControls: {
      position: "fixed",
      top: 20,
      right: 20,
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      gap: safeTheme.spacing(1.5),
      [safeTheme.breakpoints.down("sm")]: {
        position: "absolute",
        top: "max(env(safe-area-inset-top, 0px), 16px)",
        right: 16
      }
    },

    // Controle de tema — sol + chave (trilho/thumb) + lua, conforme a
    // referência aprovada (deixou de ser um único ícone isolado).
    themeToggle: {
      color: panelTokens.text.primary,
      backgroundColor: panelTokens.surfaces.raised,
      border: `1px solid ${panelTokens.borders.strong}`
    },

    themeToggleIcon: {
      color: panelTokens.text.primary
    },

    themeToggleTrack: {
      backgroundColor: panelTokens.borders.strong
    },

    themeToggleThumb: {
      backgroundColor: tokens.brand.primaryColor
    },

    passwordToggleIcon: {
      color: tokens.text.secondary
    },

    // Posicionado via `position: relative` para que o menu suspenso
    // (`languageOptions`, `position: absolute; top: 100%`) ancore sob o
    // próprio botão, e não sob o `topControls` inteiro.
    languageSurface: {
      position: "relative",
      backgroundColor: panelTokens.surfaces.raised,
      border: `1px solid ${panelTokens.borders.strong}`,
      color: panelTokens.text.primary
    },

    languageOptionsSurface: {
      backgroundColor: tokens.surfaces.base,
      border: `1px solid ${tokens.borders.subtle}`,
      color: tokens.text.primary,
      boxShadow: tokens.shadows.md
    },

    registerLinkColor: {
      color: tokens.brand.primaryColor
    },

    // Mesmo raciocínio do topControls acima (LOGIN-2.1): fixo no desktop,
    // `absolute` (acompanha a rolagem) no mobile/altura reduzida — respeita
    // a safe area inferior do dispositivo.
    versionInfoSurface: {
      position: "fixed",
      bottom: "20px",
      right: "20px",
      zIndex: 1000,
      backgroundColor: tokens.surfaces.subtle,
      border: `1px solid ${tokens.borders.subtle}`,
      color: tokens.text.secondary,
      [safeTheme.breakpoints.down("sm")]: {
        position: "absolute",
        bottom: "max(env(safe-area-inset-bottom, 0px), 20px)"
      }
    }
  };
};

export default createInfoChatLoginStyles;
